import { AIActionRequest } from './actions/aiActionBinding';

export enum CopilotPanelContext {
  Generic = 1,
  AnnualPlan = 2,
  Library = 3,
  TrialWelcome = 4,
  Processes = 5,
  ControlEvidenceAudit = 6,
  ControlDocumentationAudit = 7,
  TaskEvidenceAudit = 8,
  AuditPreparation = 9,
  FrameworkAudit = 10,
  Projects = 11,
  // Drafting an automation in SkillControl (phases 22 and 27). Not a scene and no definition of its own: the key of
  // the drafting process's usage state (useCopilotPanelUsageStates), which the three definitions that list Draft
  // automation share, and the context key the backend routes to the automation agent's chat usage.
  AutomationDrafting = 12,
  // the KPI report process (PLAN-070): a usage state only, the Generic usage runs it
  KpiReport = 13,
  // the two Supplier investigations (PLAN-073): a usage state only, the Generic usage runs them
  Supplier = 14,
  // the entity evidence audit (PLAN-077): a usage state only, the Generic usage runs it
  EntityEvidenceAudit = 15,
  NotAvailable = 999,
}

// Not a panel scene: the context key of the embedded editor's ingest sessions (Edit with AI from the library, an
// external instruction run, the editor handoff). The panel restores only sessions of the active scene's key (on scenes
// without a usage the Generic key per scene, getGenericSessionContextKey), so it never adopts them.
export const CopilotLibraryEditorContextKey = 'LibraryEditor';

export type CopilotBridgeState = {
  isOpen: boolean;
  isPinned: boolean;
  activeContext: CopilotPanelContext;
  isLoading: boolean;
  isBusy: boolean;
};

export type CopilotContextOwner = symbol;

type CopilotContextRegistration = {
  context: CopilotPanelContext;
  content: CopilotPanelContentBase;
};

const unsupportedContextCleanupDelayMs = 250;

export class CopilotBridge {
  // Initialize with state change callback to notify when bridge state changes (e.g. panel open/close, context change) for rerendering.
  //
  // Business rules and user flows:
  // - Scenes that support Copilot must register through `useCopilotContext`, which calls `activateContext`.
  // - Normal supported scenes only publish context; they only open the panel automatically when it is pinned and the
  //   user has not explicitly closed the panel.
  // - Explicit flows can still force the panel open/pinned through the hook options, for example trial welcome.
  // - When navigating between two supported scenes, the panel stays open and receives the new scene content.
  // - When the last supported scene releases its context, unsupported cleanup is deferred briefly so a new scene can claim
  //   Copilot during the same React route transition. If no scene claims it, the bridge closes the panel even when pinned.
  // - Pinning remains a user preference for supported scenes; navigating to an unsupported scene closes the panel but does
  //   not clear the stored pin preference.
  // - Closing because a scene is unsupported is transient. It must not mark the panel as user-closed, so a pinned panel can
  //   open again when the user returns to a supported scene.
  // - Closing from the panel close button, panel dismiss, navbar toggle, or hotkey is user intent. The UI persists that
  //   intent and restores it with `restoreUserClosedState`, so pinned auto-open stays disabled until the navbar/hotkey opens
  //   the panel again or a flow uses forceOpen.
  // - The panel component owns rendering: supported contexts render their content, unsupported contexts render the
  //   not-available state, and disabled AI renders the activation state.
  constructor(
    private readonly onStateChange?: (state: CopilotBridgeState, changed: Partial<CopilotBridgeState>) => void,
  ) {}

  private readonly contextRegistrations = new Map<CopilotContextOwner, CopilotContextRegistration>();

  private activeContextOwner: CopilotContextOwner | undefined = undefined;

  private pendingContextRelease: ReturnType<typeof setTimeout> | undefined = undefined;

  private setBridgeState(changed: Partial<CopilotBridgeState>): void {
    this.onStateChange?.(
      {
        isOpen: this._isOpen,
        isPinned: this.isPinned,
        activeContext: this._activeContext,
        isLoading: this._isLoading,
        isBusy: this._isBusy,
      },
      changed,
    );
  }

  //active context
  private _activeContext: CopilotPanelContext = CopilotPanelContext.NotAvailable;

  get activeContext(): CopilotPanelContext {
    return this._activeContext;
  }

  // The content the active scene published. A scene menu needs it to ask the usage definition which
  // actions it offers, without going through the panel.
  private _activeContent: CopilotPanelContentBase | undefined = undefined;

  get activeContent(): CopilotPanelContentBase | undefined {
    return this._activeContent;
  }

  // The rows the active scene has selected, kept with the scene registration that was active when they were set,
  // so a scene opened over it (a modal with its own registration) never reads them. Read when a panel action is
  // clicked; not bridge state, so setting it re-renders nothing.
  private sceneSelection: { setter: object; owner: CopilotContextOwner | undefined; ids: number[] } | undefined =
    undefined;

  get sceneSelectedEntityIds(): number[] | undefined {
    if (!this.sceneSelection || this.sceneSelection.owner !== this.activeContextOwner) return undefined;

    return this.sceneSelection.ids;
  }

  // Called by the scene's AI menu hook on every selection change; `setter` identifies that hook instance
  public setSceneSelection(setter: object, ids: number[]): void {
    this.sceneSelection = { setter, owner: this.activeContextOwner, ids: [...ids] };
  }

  // The hook that set the selection unmounts: only its own selection goes
  public clearSceneSelection(setter: object): void {
    if (this.sceneSelection?.setter === setter) {
      this.sceneSelection = undefined;
    }
  }

  private panelContextHandler: ((context: CopilotPanelContext, content: CopilotPanelContentBase) => void) | null = null;

  private actionHandler: ((request: AIActionRequest) => void | Promise<void>) | null = null;

  private navigationHandler: ((route: string) => void) | null = null;

  public registerPanel(
    handler: (context: CopilotPanelContext, content: CopilotPanelContentBase) => void,
  ): CopilotSubscription {
    this.panelContextHandler = handler;
    const activeRegistration = this.getActiveContextRegistration();
    if (activeRegistration) {
      handler(activeRegistration.context, activeRegistration.content);
    }

    return new CopilotSubscription(() => {
      if (this.panelContextHandler === handler) this.panelContextHandler = null;
    });
  }

  public registerActionHandler(handler: (request: AIActionRequest) => void | Promise<void>): CopilotSubscription {
    this.actionHandler = handler;

    return new CopilotSubscription(() => {
      if (this.actionHandler === handler) this.actionHandler = null;
    });
  }

  // Opens the panel and asks it to run one binding: a usage's action or a field's text action, with the
  // field the surface was bound to. The bridge relays the request and inspects nothing; what the binding
  // means, and when the panel is ready to run it, is the panel's. One run at a time through the panel: while it runs
  // (the test of isCopilotBusy) the request only opens the panel on the running answer and its Stop.
  public requestAction(request: AIActionRequest): void {
    this.openFromUser();
    if (this._isBusy || this._isLoading) return;

    void this.actionHandler?.(request);
  }

  // The app routes with a BrowserRouter, so there is no history to push from a plain class: a
  // component inside the router hands the bridge its own navigate.
  public registerNavigationHandler(handler: (route: string) => void): CopilotSubscription {
    this.navigationHandler = handler;

    return new CopilotSubscription(() => {
      if (this.navigationHandler === handler) this.navigationHandler = null;
    });
  }

  /**
   * Takes the user to where an action belongs, so the result appears on the page they end up on.
   *
   * In-app paths only: a route that could leave the app is ignored rather than followed.
   */
  public requestNavigation(route: string): void {
    if (!route.startsWith('/') || route.startsWith('//')) return;

    this.navigationHandler?.(route);
  }

  // Registers or refreshes Copilot support for one mounted scene instance.
  // Scene components should normally use `useCopilotContext` instead of calling this directly.
  public activateContext(
    owner: CopilotContextOwner,
    context: CopilotPanelContext,
    content: CopilotPanelContentBase,
  ): void {
    const isActivatingFromUnsupportedScene =
      this.contextRegistrations.size === 0 && this.activeContextOwner === undefined;
    this.cancelPendingContextRelease();
    this.contextRegistrations.delete(owner);
    this.contextRegistrations.set(owner, { context, content });
    this.activeContextOwner = owner;
    this.publishContext(context, content);
    if (isActivatingFromUnsupportedScene && this.isPinned && !this.isPinnedAutoOpenDismissed) {
      this.isOpen = true;
    }
  }

  // Releases Copilot support for one mounted scene instance. If another supported scene is still registered,
  // that scene becomes active; otherwise the panel is closed after the current route transition has a chance
  // to register a replacement context.
  public releaseContext(owner: CopilotContextOwner): void {
    if (!this.contextRegistrations.has(owner)) return;

    const wasActiveContext = this.activeContextOwner === owner;
    this.contextRegistrations.delete(owner);
    if (this.sceneSelection?.owner === owner) {
      this.sceneSelection = undefined;
    }
    if (!wasActiveContext) {
      if (this.contextRegistrations.size === 0) {
        this.activeContextOwner = undefined;
        this.scheduleUnsupportedContextCleanup();
      }

      return;
    }

    const fallbackContext = this.getLastContextRegistration();
    if (fallbackContext) {
      this.activeContextOwner = fallbackContext.owner;
      this.publishContext(fallbackContext.registration.context, fallbackContext.registration.content);

      return;
    }

    this.activeContextOwner = undefined;
    this.scheduleUnsupportedContextCleanup();
  }

  private publishContext(context: CopilotPanelContext, content: CopilotPanelContentBase): void {
    const contextChanged = this._activeContext !== context;
    const contentChanged = this._activeContent !== content;
    this._activeContext = context;
    this._activeContent = content;
    if (contextChanged) {
      this.setBridgeState({ activeContext: context });
    } else if (contentChanged) {
      // Same scene, new content: the actions it offers can differ (an audit that reaches Executing
      // gains one), so consumers of activeContent have to re-render.
      this.setBridgeState({});
    }
    this.panelContextHandler?.(context, content);
  }

  // Clears all scene registrations and closes the panel. Use this for global invalidation, such as signing out;
  // scene navigation should go through `releaseContext` so supported-to-supported handoffs do not flash.
  public clearContext(): void {
    this.deactivateContext();
  }

  // Route changes can land on unsupported scenes that do not register a replacement context.
  // Defer cleanup so supported route-to-route transitions still have a chance to claim the bridge.
  public closeIfUnsupported(): void {
    this.scheduleUnsupportedContextCleanup();
  }

  // Notifies Copilot consumers that external state affecting the panel changed, without making the bridge own that state.
  public notifyStateChanged(): void {
    this.setBridgeState({});
  }

  private getLastContextRegistration():
    | { owner: CopilotContextOwner; registration: CopilotContextRegistration }
    | undefined {
    let latestContext: { owner: CopilotContextOwner; registration: CopilotContextRegistration } | undefined = undefined;
    this.contextRegistrations.forEach((registration, owner) => {
      latestContext = { owner, registration };
    });

    return latestContext;
  }

  private getActiveContextRegistration(): CopilotContextRegistration | undefined {
    if (this.activeContextOwner === undefined) return undefined;

    return this.contextRegistrations.get(this.activeContextOwner);
  }

  private scheduleUnsupportedContextCleanup(): void {
    this.cancelPendingContextRelease();
    this.pendingContextRelease = setTimeout(() => {
      this.pendingContextRelease = undefined;
      if (this.getActiveContextRegistration() || this.contextRegistrations.size > 0) return;
      this.deactivateContext();
    }, unsupportedContextCleanupDelayMs);
  }

  private cancelPendingContextRelease(): void {
    if (this.pendingContextRelease === undefined) return;
    clearTimeout(this.pendingContextRelease);
    this.pendingContextRelease = undefined;
  }

  private deactivateContext(): void {
    this.cancelPendingContextRelease();
    this.contextRegistrations.clear();
    this.sceneSelection = undefined;
    this.activeContextOwner = undefined;
    this.isOpen = false;
    this.isLoading = false;
    this.publishContext(CopilotPanelContext.NotAvailable, new CopilotEmptyContent());
  }

  // Properties that can trigger panel state change for rerendering, e.g. open/close, pin/unpin, loading state, etc.
  private _isOpen: boolean = false;

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    if (this._isOpen === value) return;
    if (value) {
      this.isPinnedAutoOpenDismissed = false;
    }
    this._isOpen = value;
    this.setBridgeState({ isOpen: value });
  }

  public restoreUserClosedState(value: boolean): void {
    this.isPinnedAutoOpenDismissed = value;
    if (value) {
      this.isOpen = false;
    }
  }

  public openFromUser(): void {
    this.isPinnedAutoOpenDismissed = false;
    this.isOpen = true;
  }

  public closeFromUser(): void {
    this.isPinnedAutoOpenDismissed = true;
    this.isOpen = false;
  }

  private _isPinned: boolean = false;

  private _forcedPinned: boolean | undefined = undefined;

  get isPinned() {
    return this._forcedPinned ?? this._isPinned;
  }

  set isPinned(value: boolean) {
    if (this._isPinned === value) return;
    this.isPinnedAutoOpenDismissed = false;
    this._isPinned = value;
    this.setBridgeState({ isPinned: this.isPinned });
  }

  private isPinnedAutoOpenDismissed: boolean = false;

  get isPinnedForced() {
    return this._forcedPinned !== undefined;
  }

  public forcePinned(value: boolean | undefined): void {
    if (this._forcedPinned === value) return;
    if (this._forcedPinned === true && value === undefined && this._isOpen) {
      this._isPinned = true;
    }
    this._forcedPinned = value;
    this.setBridgeState({ isPinned: this.isPinned });
  }

  private _isLoading: boolean = false;

  get isLoading() {
    return this._isLoading;
  }

  set isLoading(value: boolean) {
    if (this._isLoading === value) return;
    this._isLoading = value;
    this.setBridgeState({ isLoading: value });
  }

  // The panel's own loading or streaming state, mirrored for the scene's AI entry points (one run at a time through
  // the panel); set and cleared by the panel only.
  private _isBusy: boolean = false;

  get isBusy() {
    return this._isBusy;
  }

  public setBusy(value: boolean): void {
    if (this._isBusy === value) return;
    this._isBusy = value;
    this.setBridgeState({ isBusy: value });
  }
}

export class CopilotSubscription {
  private isDisposed = false;

  constructor(private readonly disposeFn: () => void) {}

  public dispose(): void {
    if (this.isDisposed) return;
    this.isDisposed = true;
    this.disposeFn();
  }
}

export abstract class CopilotPanelContentBase {
  public abstract readonly context: CopilotPanelContext;
}

export class CopilotEmptyContent extends CopilotPanelContentBase {
  public readonly context = CopilotPanelContext.NotAvailable;
}
